'use client';

import { useEffect } from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { IFlightsEnrichmentData } from '../../shared/flight/domain/models/IEnrichedFlight';
import { FlightsOverview } from './FlightsOverview';
import { FlightSearch } from '../../search/ui/FlightSearch';
import { FlightsScopeToggle } from './FlightsScopeToggle';
import { useFlightStores } from '../../shared/context/flightStoresContext';
import { useSetFlightsEnrichmentData } from '../../shared/flight/ui/useSetFlightsEnrichmentData';
import { FlightListScope } from '../domain/constants/FlightListScope';
import { ContentWrap, Css, ITheme, Responsive } from '../../../../ui';
import { Color } from '../../../../styles/Color';
import { AirportCodes } from '../../../../types/airports';

interface IFlightPageProps {
	flightsEnrichmentData: IFlightsEnrichmentData;
	paramsScope?: FlightListScope;
	airport?: AirportCodes;
}

export const FlightsPageContent = observer(
	({ flightsEnrichmentData, paramsScope, airport }: IFlightPageProps) => {
		useSetFlightsEnrichmentData(flightsEnrichmentData);
		const { flightListStore } = useFlightStores();

		useEffect(() => {
			async function init() {
				const { totalResults } = await flightListStore.initialLoad();

				if (!paramsScope && totalResults === 0) {
					flightListStore.setScope(FlightListScope.Arrivals);
				}
			}

			init();
		}, []);

		return (
			<>
				<StyledContentWrap>
					<SearchBox>
						{airport && <FlightsScopeToggle airport={airport} />}
						{airport && (
							<FlightSearch airport={airport} scope={flightListStore.scope} />
						)}
					</SearchBox>
				</StyledContentWrap>
				<FlightsOverview airportCode={airport} />
			</>
		);
	},
);

const StyledContentWrap = styled(ContentWrap)`
	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-left: ${Css.pxToRem(8)} !important;
		margin-right: ${Css.pxToRem(8)} !important;
		margin-bottom: ${Css.pxToRem(16)};
	}
`;

export const SearchBox = styled.div<{}, ITheme>`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: ${Css.pxToRem(24)};
	background: ${(props) => props.theme.background?.default};
	border: 1px solid ${Color.BorderCard};
	border-radius: ${Css.pxToRem(16)};
	padding: ${Css.pxToRem(32)};
	padding-top: ${Css.pxToRem(24)};
	margin-top: -${Css.pxToRem(56)};
	z-index: 2;

	width: 100%;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding: ${Css.pxToRem(24)};
		gap: ${Css.pxToRem(16)};
		margin-top: -${Css.pxToRem(96)};
		margin-bottom: ${Css.pxToRem(16)};
		width: 100%;
	}
`;
